/**
 * Appendix Page - 附錄
 * UI: 2 主頁籤 + 各 4 子頁籤
 *   主頁籤一: Appendix 1~4
 *   主頁籤二: Appendix 5~8
 * Appendix 1: 參與者在線清單（JSON 陣列）
 * Appendix 2~5: 富文本
 * Appendix 6~8: ℹ 提示 + 富文本說明 + 檔案上傳
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import appendixService, {
  AppendixDetail, Participant, AppendixDetailUpdate,
} from '../services/appendixService';
import fileAttachmentsService, { FileAttachment } from '../services/fileAttachmentsService';
import { requestTransactionToken } from '../services/transactionService';
import { getSystemCodes } from '../services/systemCodeService';
import type { SystemCode } from '../services/systemCodeService';
import FunctionPageHeader from '../components/FunctionPageHeader';

// ── Types ─────────────────────────────────────────────────────────
type MainTab = 'm1' | 'm2';
type SubTab  = 'app1' | 'app2' | 'app3' | 'app4' | 'app5' | 'app6' | 'app7' | 'app8';

// ── useSaveFeedback ───────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ── helpers ───────────────────────────────────────────────────────
function isParticipantIncomplete(p: Participant): boolean {
  return !p.address && !p.telephone && !p.mobile && !p.email && !p.website && !p.contact_person;
}

function parseParticipants(json: string | null): Participant[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

// ── ParticipantModal ─────────────────────────────────────────────
interface ParticipantModalProps {
  parties: SystemCode[];
  initial: Participant | null;
  isZh: boolean;
  t: (k: string) => string;
  onClose: () => void;
  onSave: (p: Participant) => void;
}

const EMPTY_PARTICIPANT: Participant = {
  org_name: '', country: null, address: null, telephone: null,
  mobile: null, email: null, website: null, contact_person: null,
};

const ParticipantModal: React.FC<ParticipantModalProps> = ({ parties, initial, isZh, t, onClose, onSave }) => {
  const [form, setForm] = useState<Participant>(initial ?? { ...EMPTY_PARTICIPANT });
  const [err, setErr] = useState('');

  const set = (key: keyof Participant, value: string | number | null) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    if (!form.org_name.trim()) { setErr(t('appendix.orgNameRequired')); return; }
    onSave(form);
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '7px 10px', border: '1px solid #d9d9d9',
    borderRadius: 5, fontSize: 13, boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: '#333', marginBottom: 4, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', width: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>
          {initial ? t('appendix.editParticipant') : t('appendix.addParticipant')}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>{t('appendix.orgName')} <span style={{ color: '#e53935' }}>*</span></label>
            <input style={fieldStyle} value={form.org_name} onChange={e => set('org_name', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>{t('appendix.country')}</label>
            <select style={fieldStyle} value={form.country ?? ''} onChange={e => set('country', e.target.value ? Number(e.target.value) : null)}>
              <option value="">—</option>
              {parties.map(p => {
                const cname = p.code_cname || '';
                const ename = p.code_ename || '';
                const display = isZh
                  ? (ename ? `(${p.code}) ${cname} (${ename})` : `(${p.code}) ${cname}`)
                  : `(${p.code}) ${ename || cname}`;
                return <option key={p.id} value={p.id}>{display}</option>;
              })}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t('appendix.contactPerson')}</label>
            <input style={fieldStyle} value={form.contact_person ?? ''} onChange={e => set('contact_person', e.target.value || null)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>{t('appendix.address')}</label>
            <input style={fieldStyle} value={form.address ?? ''} onChange={e => set('address', e.target.value || null)} />
          </div>
          <div>
            <label style={labelStyle}>{t('appendix.telephone')}</label>
            <input style={fieldStyle} value={form.telephone ?? ''} onChange={e => set('telephone', e.target.value || null)} />
          </div>
          <div>
            <label style={labelStyle}>{t('appendix.mobile')}</label>
            <input style={fieldStyle} value={form.mobile ?? ''} onChange={e => set('mobile', e.target.value || null)} />
          </div>
          <div>
            <label style={labelStyle}>{t('appendix.email')}</label>
            <input style={fieldStyle} value={form.email ?? ''} onChange={e => set('email', e.target.value || null)} />
          </div>
          <div>
            <label style={labelStyle}>{t('appendix.website')}</label>
            <input style={fieldStyle} value={form.website ?? ''} onChange={e => set('website', e.target.value || null)} />
          </div>
        </div>

        {err && <div style={{ marginTop: 12, color: '#e53935', fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
          <button onClick={onClose} style={{ padding: '7px 20px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>
            {t('appendix.cancel')}
          </button>
          <button onClick={handleSubmit} style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: '#16a085', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {t('appendix.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── App1Tab ───────────────────────────────────────────────────────
interface App1TabProps {
  projectId: number;
  initialJson: string | null;
  parties: SystemCode[];
  canEdit: boolean;
  isZh: boolean;
  t: (k: string) => string;
  onSaved: (d: AppendixDetail) => void;
}

const App1Tab: React.FC<App1TabProps> = ({ projectId, initialJson, parties, canEdit, isZh, t, onSaved }) => {
  const { msg, show } = useSaveFeedback();
  const [participants, setParticipants] = useState<Participant[]>(() => parseParticipants(initialJson));
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  useEffect(() => { setParticipants(parseParticipants(initialJson)); }, [initialJson]);

  const getPartyDisplay = (id: number | null): string => {
    if (!id) return '—';
    const p = parties.find(x => x.id === id);
    if (!p) return String(id);
    const cname = p.code_cname || '';
    const ename = p.code_ename || '';
    return isZh ? (ename ? `(${p.code}) ${cname} (${ename})` : `(${p.code}) ${cname}`) : `(${p.code}) ${ename || cname}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await appendixService.saveDetail(projectId, {
        app1_content: JSON.stringify(participants),
      });
      show(true, t('appendix.saveSuccess'));
      onSaved(res);
    } catch {
      show(false, t('appendix.saveFailed'));
    } finally { setSaving(false); }
  };

  const handleModalSave = (p: Participant) => {
    if (modal.index === null) {
      setParticipants(prev => [...prev, p]);
    } else {
      setParticipants(prev => prev.map((x, i) => i === modal.index ? p : x));
    }
    setModal({ open: false, index: null });
  };

  const handleDelete = (idx: number) => {
    setParticipants(prev => prev.filter((_, i) => i !== idx));
    setDeleteIndex(null);
  };

  const cardStyle: React.CSSProperties = {
    border: '1px solid #e8e8e8', borderRadius: 8, padding: '14px 18px',
    marginBottom: 10, background: '#fafafa',
  };
  const chipStyle: React.CSSProperties = { fontSize: 12, color: '#555', background: '#f0f0f0', borderRadius: 4, padding: '2px 8px' };

  return (
    <div style={{ padding: '8px 0' }}>
      {canEdit && (
        <div style={{ marginBottom: 16 }}>
          <button onClick={() => setModal({ open: true, index: null })}
            style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #16a085', background: '#fff', color: '#16a085', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            ＋ {t('appendix.addParticipant')}
          </button>
        </div>
      )}

      {participants.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#999', fontSize: 14 }}>
          {t('appendix.noParticipants')}
        </div>
      ) : (
        participants.map((p, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#222' }}>{p.org_name}</div>
                {p.country && <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{getPartyDisplay(p.country)}</div>}
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setModal({ open: true, index: i })}
                    style={{ fontSize: 12, padding: '4px 12px', borderRadius: 5, border: '1px solid #1890ff', background: '#fff', color: '#1890ff', cursor: 'pointer' }}>
                    {t('appendix.edit')}
                  </button>
                  <button onClick={() => setDeleteIndex(i)}
                    style={{ fontSize: 12, padding: '4px 12px', borderRadius: 5, border: '1px solid #ff4d4f', background: '#fff', color: '#ff4d4f', cursor: 'pointer' }}>
                    {t('appendix.delete')}
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {p.contact_person && <span style={chipStyle}>👤 {p.contact_person}</span>}
              {p.telephone && <span style={chipStyle}>📞 {p.telephone}</span>}
              {p.mobile && <span style={chipStyle}>📱 {p.mobile}</span>}
              {p.email && <span style={chipStyle}>✉ {p.email}</span>}
              {p.website && <span style={chipStyle}>🌐 {p.website}</span>}
              {p.address && <span style={chipStyle}>📍 {p.address}</span>}
            </div>
            {isParticipantIncomplete(p) && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#d97706', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4, padding: '4px 10px' }}>
                ⚠ {t('appendix.incompleteInfo')}
              </div>
            )}
          </div>
        ))
      )}

      {canEdit && participants.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 24px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#16a085', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {saving ? t('appendix.saving') : t('appendix.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}

      {modal.open && (
        <ParticipantModal
          parties={parties}
          initial={modal.index !== null ? participants[modal.index] : null}
          isZh={isZh}
          t={t}
          onClose={() => setModal({ open: false, index: null })}
          onSave={handleModalSave}
        />
      )}

      {deleteIndex !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '24px 28px', minWidth: 340, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 14, marginBottom: 20 }}>{t('appendix.deleteConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteIndex(null)} style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>{t('appendix.cancel')}</button>
              <button onClick={() => handleDelete(deleteIndex)} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>{t('appendix.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── TextTab ───────────────────────────────────────────────────────
interface TextTabProps {
  projectId: number;
  fieldKey: keyof AppendixDetailUpdate;
  initialValue: string | null;
  canEdit: boolean;
  hint?: string;
  t: (k: string) => string;
  onSaved: (d: AppendixDetail) => void;
}

const TextTab: React.FC<TextTabProps> = ({ projectId, fieldKey, initialValue, canEdit, hint, t, onSaved }) => {
  const { msg, show } = useSaveFeedback();
  const [value, setValue] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(initialValue || ''); }, [initialValue]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await appendixService.saveDetail(projectId, { [fieldKey]: value });
      show(true, t('appendix.saveSuccess'));
      onSaved(res);
    } catch {
      show(false, t('appendix.saveFailed'));
    } finally { setSaving(false); }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      {hint && (
        <div style={{ marginBottom: 14, padding: '10px 14px', background: '#e6f7ff', border: '1px solid #91caff', borderRadius: 6, fontSize: 13, color: '#0958d9' }}>
          ℹ {hint}
        </div>
      )}
      <RichTextEditor value={value} onChange={setValue} disabled={!canEdit} minHeight="200px" />
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 24px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#16a085', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {saving ? t('appendix.saving') : t('appendix.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ── FileBlock ─────────────────────────────────────────────────────
interface FileBlockProps {
  projectId: number;
  fileIdKey: 'app6_file_id' | 'app7_file_id' | 'app8_file_id';
  currentFileId: number | null;
  canEdit: boolean;
  businessType: string;
  t: (k: string) => string;
  onSaved: (d: AppendixDetail) => void;
}

const FileBlock: React.FC<FileBlockProps> = ({ projectId, fileIdKey, currentFileId, canEdit, businessType, t, onSaved }) => {
  const { msg, show } = useSaveFeedback();
  const [fileInfo, setFileInfo] = useState<FileAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (currentFileId) {
      fileAttachmentsService.getById(currentFileId)
        .then(setFileInfo)
        .catch(() => setFileInfo(null));
    } else {
      setFileInfo(null);
    }
  }, [currentFileId]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const txn = await requestTransactionToken('appendix');
      const res = await fileAttachmentsService.upload({
        file,
        category: 'document',
        business_type: businessType,
        related_table: 'appendix_details',
        related_id: projectId,
      }, txn.txn_token);
      await fileAttachmentsService.confirm(res.file_id, txn.txn_token);
      const saved = await appendixService.saveDetail(projectId, { [fileIdKey]: res.file_id });
      show(true, t('appendix.fileUploadSuccess'));
      onSaved(saved);
    } catch {
      show(false, t('appendix.fileUploadFailed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!currentFileId) return;
    const fileIdToDelete = currentFileId;
    try {
      // 先清除 appendix 的 file_id 引用（避免 FK 衝突）
      const saved = await appendixService.saveDetail(projectId, { [fileIdKey]: null });
      show(true, t('appendix.fileDeleteSuccess'));
      onSaved(saved);
      // 背景刪除實際檔案記錄，失敗不影響 UI
      try {
        const txn = await requestTransactionToken('appendix');
        await fileAttachmentsService.delete(fileIdToDelete, false, txn.txn_token);
      } catch { /* 檔案記錄刪除失敗不影響使用者操作 */ }
    } catch {
      show(false, t('appendix.fileDeleteFailed'));
    }
    setDeleteConfirm(false);
  };

  return (
    <div style={{ marginTop: 20, padding: '16px 20px', border: '1px solid #e8e8e8', borderRadius: 8, background: '#fafafa' }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('appendix.fileUploadTitle')}</div>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>{t('appendix.fileUploadHint')}</div>

      {currentFileId && fileInfo ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#333' }}>📄 {fileInfo.original_name}</span>
          <button onClick={() => fileAttachmentsService.download(currentFileId)}
            style={{ fontSize: 12, padding: '4px 14px', borderRadius: 5, border: '1px solid #1890ff', background: '#fff', color: '#1890ff', cursor: 'pointer' }}>
            ⬇ {t('appendix.fileDownload')}
          </button>
          {canEdit && (
            <>
              <label style={{ fontSize: 12, padding: '4px 14px', borderRadius: 5, border: '1px solid #16a085', background: '#fff', color: '#16a085', cursor: uploading ? 'not-allowed' : 'pointer' }}>
                {uploading ? t('appendix.fileUploading') : t('appendix.fileReplace')}
                <input ref={inputRef} type="file" style={{ display: 'none' }} accept=".pdf,.docx,.xlsx" onChange={handleUpload} disabled={uploading} />
              </label>
              <button onClick={() => setDeleteConfirm(true)}
                style={{ fontSize: 12, padding: '4px 14px', borderRadius: 5, border: '1px solid #ff4d4f', background: '#fff', color: '#ff4d4f', cursor: 'pointer' }}>
                {t('appendix.fileDelete')}
              </button>
            </>
          )}
        </div>
      ) : (
        canEdit && (
          <label style={{ display: 'inline-block', fontSize: 13, padding: '7px 18px', borderRadius: 6, border: '1px solid #16a085', background: '#fff', color: '#16a085', cursor: uploading ? 'not-allowed' : 'pointer' }}>
            {uploading ? t('appendix.fileUploading') : `⬆ ${t('appendix.fileUpload')}`}
            <input ref={inputRef} type="file" style={{ display: 'none' }} accept=".pdf,.docx,.xlsx" onChange={handleUpload} disabled={uploading} />
          </label>
        )
      )}

      {msg && <div style={{ marginTop: 8, fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</div>}

      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '24px 28px', minWidth: 340, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 14, marginBottom: 20 }}>{t('appendix.fileDeleteConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(false)} style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>{t('appendix.cancel')}</button>
              <button onClick={handleDelete} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>{t('appendix.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── FileTextTab ───────────────────────────────────────────────────
interface FileTextTabProps {
  projectId: number;
  contentKey: 'app6_content' | 'app7_content' | 'app8_content';
  fileIdKey: 'app6_file_id' | 'app7_file_id' | 'app8_file_id';
  initialContent: string | null;
  currentFileId: number | null;
  canEdit: boolean;
  hint: string;
  businessType: string;
  t: (k: string) => string;
  onSaved: (d: AppendixDetail) => void;
}

const FileTextTab: React.FC<FileTextTabProps> = ({
  projectId, contentKey, fileIdKey, initialContent, currentFileId,
  canEdit, hint, businessType, t, onSaved,
}) => (
  <div style={{ padding: '8px 0' }}>
    <div style={{ marginBottom: 16, padding: '10px 14px', background: '#e6f7ff', border: '1px solid #91caff', borderRadius: 6, fontSize: 13, color: '#0958d9' }}>
      ℹ {hint}
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#333' }}>{t('appendix.descriptionTitle')}</div>
    <TextTab
      projectId={projectId}
      fieldKey={contentKey}
      initialValue={initialContent}
      canEdit={canEdit}
      t={t}
      onSaved={onSaved}
    />
    <FileBlock
      projectId={projectId}
      fileIdKey={fileIdKey}
      currentFileId={currentFileId}
      canEdit={canEdit}
      businessType={businessType}
      t={t}
      onSaved={onSaved}
    />
  </div>
);

// ════════════════════════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════════════════════════
const AppendixPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission('appendix', 'update');

  const [detail, setDetail] = useState<AppendixDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [parties, setParties] = useState<SystemCode[]>([]);
  const [mainTab, setMainTab] = useState<MainTab>('m1');
  const [subTab, setSubTab] = useState<SubTab>('app1');
  const [resetConfirm, setResetConfirm] = useState(false);

  // ── Load ──────────────────────────────────────────────────────
  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('AppendixPage');
      const res = await appendixService.getDetailByProject(projectId);
      setDetail(res);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    getSystemCodes({ code_etype: 'Article 6.4 Party', is_active: true })
      .then(setParties)
      .catch(() => setParties([]));
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setDetail(null); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  // ── Reset ─────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!selectedProjectId) return;
    try {
      await appendixService.resetDetail(selectedProjectId);
      await loadData(selectedProjectId);
    } catch { /* ignore */ }
    setResetConfirm(false);
  };

  // ── Tab styles ────────────────────────────────────────────────
  const mainTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 22px', fontSize: 14, fontWeight: active ? 700 : 500,
    background: active ? '#16a085' : '#f5f5f5',
    color: active ? '#fff' : '#555',
    cursor: 'pointer',
    borderTop: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    borderBottom: active ? '2px solid #fff' : '1px solid #e0e0e0',
    borderRadius: '6px 6px 0 0',
    marginRight: 4, transition: 'all 0.15s',
  });

  const subTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 18px', fontSize: 13, fontWeight: active ? 700 : 400,
    borderTop: 'none', borderLeft: 'none', borderRight: 'none',
    borderBottom: `3px solid ${active ? '#16a085' : 'transparent'}`,
    color: active ? '#16a085' : '#555',
    cursor: 'pointer', background: 'none',
    transition: 'all 0.15s',
  });

  const handleMainTab = (tab: MainTab) => {
    setMainTab(tab);
    setSubTab(tab === 'm1' ? 'app1' : 'app5');
  };

  const m1Tabs: SubTab[] = ['app1', 'app2', 'app3', 'app4'];
  const m2Tabs: SubTab[] = ['app5', 'app6', 'app7', 'app8'];
  const currentTabs = mainTab === 'm1' ? m1Tabs : m2Tabs;
  const activeSubTab: SubTab = currentTabs.includes(subTab) ? subTab : currentTabs[0];

  const fileHints: Record<string, string> = {
    app6: isZh
      ? "請依 Article 6.4 永續發展工具填寫本附件（A6.4-FORM-AC-015）。[Include the 'A6.4 Environmental and Social Safeguards Risk Assessment Form' as per the Article 6.4 sustainable development tool.]"
      : "Include the 'A6.4 Environmental and Social Safeguards Risk Assessment Form' as per the Article 6.4 sustainable development tool (A6.4-FORM-AC-015).",
    app7: isZh
      ? "請依 Article 6.4 永續發展工具填寫本附件（A6.4-FORM-AC-016）。[Include the 'A6.4 Environmental and Social Management Plan Form' as per the Article 6.4 sustainable development tool.]"
      : "Include the 'A6.4 Environmental and Social Management Plan Form' as per the Article 6.4 sustainable development tool (A6.4-FORM-AC-016).",
    app8: isZh
      ? "請依 Article 6.4 永續發展工具填寫本附件（A6.4-FORM-AC-017）。[Include the 'A6.4 Sustainable Development Impact Form' as per the Article 6.4 sustainable development tool.]"
      : "Include the 'A6.4 Sustainable Development Impact Form' as per the Article 6.4 sustainable development tool (A6.4-FORM-AC-017).",
  };

  const renderContent = () => {
    if (!selectedProjectId) return null;
    switch (activeSubTab) {
      case 'app1':
        return <App1Tab key="app1" projectId={selectedProjectId} initialJson={detail?.app1_content ?? null} parties={parties} canEdit={canUpdate} isZh={isZh} t={t} onSaved={setDetail} />;
      case 'app2':
        return <TextTab key="app2" projectId={selectedProjectId} fieldKey="app2_content" initialValue={detail?.app2_content ?? null} canEdit={canUpdate} t={t} onSaved={setDetail} />;
      case 'app3':
        return <TextTab key="app3" projectId={selectedProjectId} fieldKey="app3_content" initialValue={detail?.app3_content ?? null} canEdit={canUpdate} t={t} onSaved={setDetail} />;
      case 'app4':
        return <TextTab key="app4" projectId={selectedProjectId} fieldKey="app4_content" initialValue={detail?.app4_content ?? null} canEdit={canUpdate} t={t} onSaved={setDetail} />;
      case 'app5':
        return <TextTab key="app5" projectId={selectedProjectId} fieldKey="app5_content" initialValue={detail?.app5_content ?? null} canEdit={canUpdate} t={t} onSaved={setDetail} />;
      case 'app6':
        return <FileTextTab key="app6" projectId={selectedProjectId} contentKey="app6_content" fileIdKey="app6_file_id" initialContent={detail?.app6_content ?? null} currentFileId={detail?.app6_file_id ?? null} canEdit={canUpdate} hint={fileHints.app6} businessType="appendix_6" t={t} onSaved={setDetail} />;
      case 'app7':
        return <FileTextTab key="app7" projectId={selectedProjectId} contentKey="app7_content" fileIdKey="app7_file_id" initialContent={detail?.app7_content ?? null} currentFileId={detail?.app7_file_id ?? null} canEdit={canUpdate} hint={fileHints.app7} businessType="appendix_7" t={t} onSaved={setDetail} />;
      case 'app8':
        return <FileTextTab key="app8" projectId={selectedProjectId} contentKey="app8_content" fileIdKey="app8_file_id" initialContent={detail?.app8_content ?? null} currentFileId={detail?.app8_file_id ?? null} canEdit={canUpdate} hint={fileHints.app8} businessType="appendix_8" t={t} onSaved={setDetail} />;
      default: return null;
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="appendix" />
        {canUpdate && detail && detail.id > 0 && (
          <button onClick={() => setResetConfirm(true)}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>
            🗑 {t('appendix.resetData')}
          </button>
        )}
      </div>

      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>{t('appendix.selectProject')}</div>
      ) : loading ? (
        <div className="loading">{t('appendix.loading')}</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {/* Main Tabs */}
          <div style={{ display: 'flex', padding: '16px 20px 0', borderBottom: '1px solid #e0e0e0', background: '#fafafa', borderRadius: '8px 8px 0 0' }}>
            <button style={mainTabStyle(mainTab === 'm1')} onClick={() => handleMainTab('m1')}>{t('appendix.tabMain1')}</button>
            <button style={mainTabStyle(mainTab === 'm2')} onClick={() => handleMainTab('m2')}>{t('appendix.tabMain2')}</button>
          </div>
          {/* Sub Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', padding: '0 20px', gap: 4 }}>
            {mainTab === 'm1' && (
              <>
                <button style={subTabStyle(activeSubTab === 'app1')} onClick={() => setSubTab('app1')}>{t('appendix.tabApp1')}</button>
                <button style={subTabStyle(activeSubTab === 'app2')} onClick={() => setSubTab('app2')}>{t('appendix.tabApp2')}</button>
                <button style={subTabStyle(activeSubTab === 'app3')} onClick={() => setSubTab('app3')}>{t('appendix.tabApp3')}</button>
                <button style={subTabStyle(activeSubTab === 'app4')} onClick={() => setSubTab('app4')}>{t('appendix.tabApp4')}</button>
              </>
            )}
            {mainTab === 'm2' && (
              <>
                <button style={subTabStyle(activeSubTab === 'app5')} onClick={() => setSubTab('app5')}>{t('appendix.tabApp5')}</button>
                <button style={subTabStyle(activeSubTab === 'app6')} onClick={() => setSubTab('app6')}>{t('appendix.tabApp6')}</button>
                <button style={subTabStyle(activeSubTab === 'app7')} onClick={() => setSubTab('app7')}>{t('appendix.tabApp7')}</button>
                <button style={subTabStyle(activeSubTab === 'app8')} onClick={() => setSubTab('app8')}>{t('appendix.tabApp8')}</button>
              </>
            )}
          </div>
          {/* Content */}
          <div style={{ padding: '20px 24px' }}>{renderContent()}</div>
        </div>
      )}

      {resetConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('appendix.resetTitle')}</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20, whiteSpace: 'pre-line' }}>{t('appendix.resetConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setResetConfirm(false)} style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>{t('appendix.cancel')}</button>
              <button onClick={handleReset} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>{t('appendix.reset')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppendixPage;
